/**
 * @module CloudOnboarding
 *
 * This module provides GraphQL queries and mutations for managing cloud onboarding processes.
 * It defines three hooks, `useCloudRegionsQuery`, `useCloudOnboardingQuery`, and `useCreateCloudOnboardingMutation`,
 * to handle different aspects of cloud onboarding for an account.
 */
import { gql, useMutation, useQuery } from '@apollo/client';

import {
  CloudOnboardingInput,
  CloudProvider,
} from 'src/lib/graphqlTypes/types';

import { GraphQLTypes } from '../lib/graphqlTypes';

/**
 * GraphQL query to fetch available regions for a given cloud provider.
 * @type {DocumentNode}
 */
const CloudRegionsQuery = gql`
  query Cloud__CloudRegionsQuery($provider: CloudProvider!) {
    cloud {
      regions(provider: $provider) {
        name
        code
        provider
        state
        country
      }
    }
  }
`;

/**
 * GraphQL query to fetch onboarding details for a given account and graph reference.
 * @type {DocumentNode}
 */
const CloudOnboardingQuery = gql`
  query CloudOnboardingQuery($accountId: ID!, $graphRef: String!) {
    account(id: $accountId) {
      id
      cloudOnboarding(graphRef: $graphRef) {
        graphRef
        provider
        tier
        region {
          code
          name
        }
      }
    }
  }
`;

/**
 * GraphQL mutation to create a new cloud onboarding process.
 * @type {DocumentNode}
 */
const AddCloudOnboardingMutation = gql`
  mutation CreateCloudOnboardingMutation(
    $accountId: ID!
    $cloudOnboardingInput: CloudOnboardingInput!
  ) {
    account(id: $accountId) {
      createCloudOnboarding(input: $cloudOnboardingInput) {
        ... on CreateOnboardingSuccess {
          onboarding {
            graphRef
            provider
            tier
            region {
              name
              code
              provider
              state
              country
            }
          }
        }
        ... on InvalidInputErrors {
          errors {
            argument
            location
            reason
          }
          message
        }
        ... on InternalServerError {
          message
        }
      }
    }
  }
`;

/**
 * GraphQL query to fetch onboarding details for a given account and graph reference.
 * @type {DocumentNode}
 */
export const CloudOnboardingFragment = gql`
  fragment CloudOnboardingFragment on Account {
    id
    cloudOnboarding(graphRef: $graphRef) {
      graphRef
      provider
      tier
      region {
        code
        name
      }
    }
  }
`;

/**
 * Hook to execute the query for available cloud regions.
 *
 * @returns {Object} - An object containing:
 *                     - data: Data returned from the query.
 *                     - loading: Loading state of the query.
 */
export const useCloudRegionsQuery = () => {
  return useQuery<
    GraphQLTypes.Cloud__CloudRegionsQuery,
    GraphQLTypes.Cloud__CloudRegionsQueryVariables
  >(CloudRegionsQuery, {
    variables: {
      provider: CloudProvider.AWS,
    },
  });
};

/**
 * Hook to execute the query for cloud onboarding data.
 *
 * @param {string} accountId - The account ID.
 * @param {string} graphRef - The graph reference.
 * @returns {Object} - An object containing:
 *                     - data: Data returned from the query.
 * This query is skipped if either `accountId` or `graphRef` is falsy.
 */
export const useCloudOnboardingQuery = (
  accountId: string,
  graphRef: string,
) => {
  return useQuery<
    GraphQLTypes.CloudOnboardingQuery,
    GraphQLTypes.CloudOnboardingQueryVariables
  >(CloudOnboardingQuery, {
    variables: {
      accountId,
      graphRef,
    },
    skip: !accountId || !graphRef,
  });
};

/**
 * Hook to execute the mutation for creating a new cloud onboarding process.
 *
 * @returns {Object} - An object containing:
 *                     - createCloudOnboarding: Function to execute the mutation.
 *                     - ...createCloudOnboardingResult: Additional result attributes like loading state or errors.
 */
export const useCreateCloudOnboardingMutation = () => {
  const [createCloudOnboardingMutation, createCloudOnboardingResult] =
    useMutation<
      GraphQLTypes.CreateCloudOnboardingMutation,
      GraphQLTypes.CreateCloudOnboardingMutationVariables
    >(AddCloudOnboardingMutation);

  /**
   * Function to create a new cloud onboarding process.
   *
   * @param {Object} variables - Variables for the mutation.
   * @param {string} variables.accountId - The account ID.
   * @param {CloudOnboardingInput} variables.cloudOnboardingInput - The input object for onboarding.
   * @returns {Promise} - Returns a promise resolving to the mutation result.
   */
  const createCloudOnboarding = (variables: {
    accountId: string;
    cloudOnboardingInput: CloudOnboardingInput;
  }) => createCloudOnboardingMutation({ variables });

  return { createCloudOnboarding, ...createCloudOnboardingResult };
};
